import React, { useEffect, useState } from 'react';
import RialSymbol from '../components/RialSymbol';
import axios from '../api';
import {
  Avatar, Box, Button, Card, Chip, Dialog, DialogActions, DialogContent,
  DialogTitle, IconButton, Snackbar, Stack, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, Typography, Alert,
} from '@mui/material';
import {
  Add, Close, Delete as DeleteIcon, Edit as EditIcon, EventSeat as EventSeatIcon,
} from '@mui/icons-material';
import LoadingAnimation from '../components/LoadingAnimation';

interface BanquetHallRow {
  _id: string;
  name: string;
  description: string;
  images?: string[];
  location: { address: string; city: string };
  capacity: { min: number; max: number };
  pricePerEvent: number;
  amenities?: string[];
  contact: { phone: string; email?: string };
  status: 'pending' | 'verified' | 'rejected' | 'suspended';
  rejectionReason?: string | null;
}

const STATUS_META: Record<string, { label: string; color: 'warning' | 'success' | 'error' | 'default' }> = {
  pending: { label: 'Pending Review', color: 'warning' },
  verified: { label: 'Verified', color: 'success' },
  rejected: { label: 'Rejected', color: 'error' },
  suspended: { label: 'Suspended', color: 'error' },
};
const statusMeta = (status?: string) => STATUS_META[status || ''] || { label: status || 'Unknown', color: 'default' as const };

const emptyForm = {
  name: '',
  description: '',
  address: '',
  city: '',
  minGuests: '',
  maxGuests: '',
  pricePerEvent: '',
  amenities: '',
  contactPhone: '',
  contactEmail: '',
  imageUrl: '',
};

export default function MerchantBanquetHalls() {
  const [halls, setHalls] = useState<BanquetHallRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingHall, setEditingHall] = useState<BanquetHallRow | null>(null);
  const [formData, setFormData] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<BanquetHallRow | null>(null);

  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'warning' }>({
    open: false, message: '', severity: 'success',
  });

  useEffect(() => {
    fetchHalls();
  }, []);

  const fetchHalls = async () => {
    setLoading(true);
    setError(false);
    try {
      const response = await axios.get('/api/merchant/banquet-halls', { params: { limit: 100 } });
      setHalls(response.data.banquetHalls || []);
    } catch (err) {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  const notify = (message: string, severity: 'success' | 'error' | 'warning' = 'success') =>
    setSnackbar({ open: true, message, severity });

  const openCreate = () => {
    setEditingHall(null);
    setFormData(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (hall: BanquetHallRow) => {
    setEditingHall(hall);
    setFormData({
      name: hall.name,
      description: hall.description,
      address: hall.location?.address || '',
      city: hall.location?.city || '',
      minGuests: String(hall.capacity?.min ?? ''),
      maxGuests: String(hall.capacity?.max ?? ''),
      pricePerEvent: String(hall.pricePerEvent ?? ''),
      amenities: (hall.amenities || []).join(', '),
      contactPhone: hall.contact?.phone || '',
      contactEmail: hall.contact?.email || '',
      imageUrl: hall.images?.[0] || '',
    });
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingHall(null);
  };

  const handleSave = async () => {
    if (!formData.name.trim() || !formData.description.trim() || !formData.address.trim() ||
        !formData.city.trim() || !formData.minGuests || !formData.maxGuests || !formData.pricePerEvent ||
        !formData.contactPhone.trim()) {
      notify('Please fill in all required fields', 'error');
      return;
    }
    if (Number(formData.maxGuests) < Number(formData.minGuests)) {
      notify('Max guests must be at least the min guests', 'error');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        images: formData.imageUrl ? [formData.imageUrl] : [],
        location: { address: formData.address.trim(), city: formData.city.trim() },
        capacity: { min: parseInt(formData.minGuests, 10), max: parseInt(formData.maxGuests, 10) },
        pricePerEvent: parseFloat(formData.pricePerEvent),
        amenities: formData.amenities.split(',').map((a) => a.trim()).filter(Boolean),
        contact: { phone: formData.contactPhone.trim(), email: formData.contactEmail.trim() },
      };
      if (editingHall) {
        await axios.put(`/api/merchant/banquet-halls/${editingHall._id}`, payload);
        notify('Banquet hall updated');
      } else {
        await axios.post('/api/merchant/banquet-halls', payload);
        notify('Banquet hall submitted for verification');
      }
      closeDialog();
      fetchHalls();
    } catch (err: any) {
      notify(err?.response?.data?.error || 'Could not save this banquet hall', 'error');
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`/api/merchant/banquet-halls/${deleteTarget._id}`);
      notify(`${deleteTarget.name} removed`);
      setDeleteTarget(null);
      fetchHalls();
    } catch (err: any) {
      notify(err?.response?.data?.error || 'Could not remove this hall', 'error');
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>Banquet Halls</Typography>
          <Typography variant="body2" color="text.secondary">
            List your banquet halls so customers can request bookings. New and edited halls need admin verification.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={openCreate}>
          Add Hall
        </Button>
      </Box>

      <Card>
        {loading ? (
          <Box sx={{ py: 5 }}><LoadingAnimation /></Box>
        ) : error ? (
          <Box sx={{ py: 5, textAlign: 'center' }}>
            <Typography color="error">Failed to load your banquet halls.</Typography>
          </Box>
        ) : halls.length === 0 ? (
          <Box sx={{ py: 6, textAlign: 'center' }}>
            <EventSeatIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
            <Typography sx={{ fontWeight: 600 }}>No banquet halls yet</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Add a hall so customers can request bookings for events and celebrations.
            </Typography>
            <Button variant="contained" startIcon={<Add />} onClick={openCreate}>
              Add Hall
            </Button>
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'background.default' }}>
                  <TableCell><strong>Photo</strong></TableCell>
                  <TableCell><strong>Hall Name</strong></TableCell>
                  <TableCell><strong>City</strong></TableCell>
                  <TableCell><strong>Capacity</strong></TableCell>
                  <TableCell><strong>Price / Event</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                  <TableCell align="center"><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {halls.map((hall) => (
                  <TableRow key={hall._id} hover>
                    <TableCell>
                      <Avatar src={hall.images?.[0]} alt={hall.name} variant="rounded" sx={{ width: 40, height: 40 }}>
                        <EventSeatIcon fontSize="small" />
                      </Avatar>
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontWeight: 600 }}>{hall.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {hall.description?.substring(0, 50)}
                      </Typography>
                    </TableCell>
                    <TableCell>{hall.location?.city || '—'}</TableCell>
                    <TableCell>{hall.capacity?.min}-{hall.capacity?.max}</TableCell>
                    <TableCell><RialSymbol /> {hall.pricePerEvent?.toFixed(0)}</TableCell>
                    <TableCell>
                      <Chip label={statusMeta(hall.status).label} color={statusMeta(hall.status).color} size="small" />
                      {hall.status === 'rejected' && hall.rejectionReason && (
                        <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                          {hall.rejectionReason}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <IconButton size="small" onClick={() => openEdit(hall)} title="Edit">
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => setDeleteTarget(hall)} title="Remove">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>

      {/* Create / Edit dialog */}
      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingHall ? 'Edit Banquet Hall' : 'Add Banquet Hall'}
          <IconButton onClick={closeDialog} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {editingHall?.status === 'verified' && (
              <Alert severity="info">Changing core details sends this hall back for admin re-verification.</Alert>
            )}
            <TextField
              label="Hall Name *"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              fullWidth
            />
            <TextField
              label="Description *"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              multiline
              minRows={2}
              fullWidth
            />
            <TextField
              label="Photo URL"
              value={formData.imageUrl}
              onChange={(e) => setFormData({ ...formData, imageUrl: e.target.value })}
              placeholder="https://..."
              fullWidth
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Address *"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                fullWidth
              />
              <TextField
                label="City *"
                value={formData.city}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                fullWidth
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Min Guests *"
                type="number"
                value={formData.minGuests}
                onChange={(e) => setFormData({ ...formData, minGuests: e.target.value })}
                slotProps={{ htmlInput: { min: 0 } }}
                fullWidth
              />
              <TextField
                label="Max Guests *"
                type="number"
                value={formData.maxGuests}
                onChange={(e) => setFormData({ ...formData, maxGuests: e.target.value })}
                slotProps={{ htmlInput: { min: 0 } }}
                fullWidth
              />
              <TextField
                label="Price / Event (OMR) *"
                type="number"
                value={formData.pricePerEvent}
                onChange={(e) => setFormData({ ...formData, pricePerEvent: e.target.value })}
                slotProps={{ htmlInput: { step: 0.5, min: 0 } }}
                fullWidth
              />
            </Box>
            <TextField
              label="Amenities (comma-separated)"
              value={formData.amenities}
              onChange={(e) => setFormData({ ...formData, amenities: e.target.value })}
              placeholder="Parking, Catering, Sound System"
              fullWidth
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Contact Phone *"
                value={formData.contactPhone}
                onChange={(e) => setFormData({ ...formData, contactPhone: e.target.value })}
                fullWidth
              />
              <TextField
                label="Contact Email"
                value={formData.contactEmail}
                onChange={(e) => setFormData({ ...formData, contactEmail: e.target.value })}
                fullWidth
              />
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : editingHall ? 'Save Changes' : 'Submit for Verification'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Remove Banquet Hall</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to remove "{deleteTarget?.name}"? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={confirmDelete}>Remove</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
